import { Loader2 } from "lucide-react";

// Shared building blocks so every dashboard page looks the same.
// Palette: brand red #C00000, deep red #8B0000, ink #111111, neutral grays.

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 pb-5 border-b border-gray-200">
      <div>
        <h1 className="text-2xl md:text-[26px] font-semibold text-[#111111] tracking-tight">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-gray-500 max-w-2xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function Card({ title, action, children, className = "", padded = true }) {
  return (
    <section className={`bg-white border border-gray-200 rounded-xl ${className}`}>
      {(title || action) && (
        <header className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-[#111111] uppercase tracking-wide">{title}</h2>
          {action}
        </header>
      )}
      <div className={padded ? "p-5" : ""}>{children}</div>
    </section>
  );
}

export function Stat({ label, value, hint, tone = "default", icon }) {
  const tones = {
    default: "text-[#111111]",
    brand: "text-[#C00000]",
    good: "text-emerald-700",
    warn: "text-amber-600",
  };
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="flex items-start justify-between">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
        {icon && <span className="text-gray-400">{icon}</span>}
      </div>
      <p className={`mt-3 text-3xl font-semibold tabular-nums ${tones[tone]}`}>{value}</p>
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

const badgeTones = {
  neutral: "bg-gray-100 text-gray-700",
  brand: "bg-red-50 text-[#C00000]",
  good: "bg-emerald-50 text-emerald-700",
  warn: "bg-amber-50 text-amber-700",
  bad: "bg-red-50 text-[#8B0000]",
};
export function Badge({ tone = "neutral", children }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeTones[tone]}`}>
      {children}
    </span>
  );
}

export function Button({ variant = "primary", size = "md", loading, className = "", children, ...rest }) {
  const variants = {
    primary: "bg-[#C00000] text-white hover:bg-[#8B0000] disabled:bg-gray-300",
    dark: "bg-[#111111] text-white hover:bg-black disabled:bg-gray-300",
    outline: "bg-white text-gray-700 border border-gray-300 hover:border-[#C00000] hover:text-[#C00000] disabled:opacity-50",
    ghost: "text-gray-600 hover:bg-gray-100 disabled:opacity-50",
    danger: "bg-white text-[#8B0000] border border-red-200 hover:bg-red-50 disabled:opacity-50",
  };
  const sizes = { sm: "px-3 py-1.5 text-xs", md: "px-4 py-2 text-sm" };
  return (
    <button
      {...rest}
      disabled={loading || rest.disabled}
      className={`inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C00000]/40 ${variants[variant]} ${sizes[size]} ${className}`}
    >
      {loading && <Loader2 size={14} className="animate-spin" />}
      {children}
    </button>
  );
}

export const inputClass =
  "w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:border-[#C00000] focus:ring-2 focus:ring-[#C00000]/15";

export function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-600 mb-1.5">{label}</span>
      {children}
      {hint && <span className="block text-xs text-gray-400 mt-1">{hint}</span>}
    </label>
  );
}

export function Spinner({ label = "Loading" }) {
  return (
    <div className="flex items-center justify-center gap-2 py-20 text-gray-500 text-sm">
      <Loader2 size={18} className="animate-spin text-[#C00000]" /> {label}…
    </div>
  );
}

export function ErrorNote({ children }) {
  if (!children) return null;
  return (
    <div className="px-3 py-2 rounded-lg bg-red-50 border border-red-100 text-sm text-[#8B0000]">{children}</div>
  );
}

export function Empty({ children }) {
  return <p className="text-sm text-gray-500 py-8 text-center">{children}</p>;
}

export function Modal({ title, onClose, children, width = "max-w-lg" }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className={`relative bg-white rounded-xl border border-gray-200 shadow-xl w-full ${width} max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-[#111111]">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none" aria-label="Close">×</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
